import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import OrderStatus from './OrderStatus';
import styles from './SuccessPage.module.css';
import type {
  OrderStatus as OrderStatusType,
  ShippingStatus as ShippingStatusType,
} from '../types';

interface SuccessPageProps {
  onCartRefresh: () => void;
}

interface OrderData {
  id: string;
  orderNumber: string;
  status: string;
  shippingStatus: string;
  customerEmail: string;
  orderItems: Array<{
    name: string;
    quantity: number;
    size?: string;
    price: number;
  }>;
  stripeAmountTotal?: number; // Real amount from Stripe (includes tax, shipping, etc.)
  totalAmount: number; // Original calculated amount
}

const SuccessPage: React.FC<SuccessPageProps> = ({ onCartRefresh }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [orderData, setOrderData] = useState<OrderData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchOrderData = async () => {
      try {
        // Get session ID from URL params
        const sessionId = searchParams.get('session_id');

        if (!sessionId) {
          setError('No session ID found');
          setLoading(false);
          return;
        }

        console.log('🔍 Fetching order data for session:', sessionId);

        // Determine the API endpoint based on the current domain
        const isTest = window.location.hostname.includes('test');
        const apiBase = isTest
          ? 'https://yokeojbh71.execute-api.us-east-1.amazonaws.com/test'
          : 'https://chz1yzlddg.execute-api.us-east-1.amazonaws.com/prod';

        // Fetch order data from API
        const response = await fetch(
          `${apiBase}/api/order?session_id=${sessionId}`
        );

        if (!response.ok) {
          if (response.status === 404) {
            setError(
              'Order not found. Please check your email for confirmation.'
            );
          } else {
            setError('Failed to fetch order details');
          }
          setLoading(false);
          return;
        }

        const order = await response.json();
        console.log('✅ Order data fetched:', order);

        // Transform the order data for display
        const transformedOrder: OrderData = {
          id: order.orderNumber,
          orderNumber: order.orderNumber,
          status: order.status || 'paid',
          shippingStatus: order.shippingStatus || 'pending',
          customerEmail: order.customerEmail || 'N/A',
          orderItems: order.items || [],
          stripeAmountTotal: order.stripeAmountTotal,
          totalAmount: order.totalAmount || 0,
        };

        setOrderData(transformedOrder);
        setLoading(false);
      } catch (error) {
        console.error('❌ Error fetching order data:', error);
        setError('Failed to load order details');
        setLoading(false);
      }
    };

    fetchOrderData();
  }, [searchParams]);

  // Show loading state
  if (loading) {
    return (
      <div className={styles.successPage}>
        <div className={styles.successContainer}>
          <div className={styles.successCard}>
            <div className={styles.successHeader}>
              <h1 className={styles.successTitle}>Loading Order Details...</h1>
            </div>
            <p className={styles.successMessage}>
              Please wait while we fetch your order information...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className={styles.successPage}>
        <div className={styles.successContainer}>
          <div className={styles.successCard}>
            <div className={styles.successHeader}>
              <h1 className={styles.successTitle}>Payment Successful!</h1>
            </div>
            <p className={styles.successMessage}>
              Thank you for your purchase! Your payment has been processed
              successfully.
            </p>
            <div className={styles.errorMessage}>
              <p>⚠️ {error}</p>
              <p>Please check your email for order confirmation details.</p>
            </div>
            <button
              onClick={() => navigate('/')}
              className={styles.continueShoppingButton}
            >
              Continue Shopping
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Calculate the display amount - use Stripe amount if available, otherwise fall back to calculated
  const displayAmount = orderData?.stripeAmountTotal
    ? (orderData.stripeAmountTotal / 100).toFixed(2) // Stripe amounts are in cents
    : orderData?.orderItems
        .reduce((sum, item) => sum + item.price * item.quantity, 0)
        .toFixed(2);

  return (
    <div className={styles.successPage}>
      <div className={styles.successContainer}>
        {/* Success Icon and Title */}
        <div className={styles.successCard}>
          <div className={styles.successHeader}>
            <div className={styles.successIcon}>
              <svg
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h1 className={styles.successTitle}>Payment Successful!</h1>
          </div>
          <p className={styles.successMessage}>
            Thank you for your purchase! Your payment has been processed
            successfully.
          </p>

          {/* Spam Disclaimer */}
          <div className={styles.spamDisclaimer}>
            <p className={styles.spamDisclaimerText}>
              📧 <strong>Important:</strong> Please check your spam/junk folder
              if you don't receive your order confirmation email within a few
              minutes.
            </p>
          </div>

          {/* Order Details */}
          {orderData && (
            <div className={styles.orderDetails}>
              <h3 className={styles.orderDetailsTitle}>Order Details:</h3>
              <p className={styles.orderDetailItem}>
                <span className={styles.orderDetailLabel}>Order Number:</span>{' '}
                {orderData.orderNumber}
              </p>
              {orderData.customerEmail && (
                <p className={styles.orderDetailItem}>
                  <span className={styles.orderDetailLabel}>Email:</span>{' '}
                  {orderData.customerEmail}
                </p>
              )}
              <p className={styles.orderDetailItem}>
                <span className={styles.orderDetailLabel}>Order Date:</span>{' '}
                {new Date().toLocaleDateString()}
              </p>
              <p className={styles.orderDetailItem}>
                <span className={styles.orderDetailLabel}>Confirmation:</span>{' '}
                Sent to your email
              </p>
              <p className={styles.orderDetailItem}>
                <span className={styles.orderDetailLabel}>Total Amount:</span> $
                {displayAmount}
                {orderData.stripeAmountTotal && (
                  <span className={styles.amountNote}>
                    {' '}
                    (includes tax & shipping)
                  </span>
                )}
              </p>
            </div>
          )}

          <button
            onClick={() => navigate('/')}
            className={styles.continueShoppingButton}
          >
            Continue Shopping
          </button>
        </div>

        {/* Order Status */}
        {orderData && (
          <div className={styles.orderStatusSection}>
            <OrderStatus
              orderStatus={orderData.status as OrderStatusType}
              shippingStatus={orderData.shippingStatus as ShippingStatusType}
              orderItems={orderData.orderItems.map(item => ({
                ...item,
                size: item.size || 'N/A',
              }))}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default SuccessPage;
